import useFeatureFlagsStore from '@/composables/useFeatureFlags.store'
import type { User } from '@/portalApi'
import { setUserContext } from '@/sentry'
import { CodeVerifier } from '@/stores/auth/accessors'
import { postTokenRequest, TokenEndpointError } from '@/stores/auth/functions'
import { generateLoginUrl, generateLogoutUrl, generateTokenEndpoint, OIDC_SCOPES } from '@/stores/auth/urls'
import { useUsersStore } from '@/stores/users.store'
import { LOCAL_TIME_ZONE } from '@/util/dates'
import getEnv from '@/util/env'
import { can as _can, canMutate as _canMutate } from '@/util/roles'
import type { Capability } from '@/util/roles/capabilities'
import { ALL_ROLES, type Operation } from '@/util/roles/generated'
import Toasts from '@/util/toasts'
import { type RemovableRef, useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, type ComputedRef, ref, type Ref, watch } from 'vue'

const initiated = { expiryCheck: false }

/**
 * Calculates the date and time that is a specified number of seconds from the current time.
 *
 * @param seconds - The number of seconds from the current time to calculate.
 * @returns A new Date object representing the calculated time.
 */
function dateInSecondsFromNow(seconds: number): Date {
  const now = new Date(),
    expiryDate = new Date()
  expiryDate.setSeconds(now.getSeconds() + seconds)
  return expiryDate
}

/**
 * This store is used for persisting user account data, both for a general list
 * of assignees and for the current user's account info.
 *
 * The pattern here is to fetch all users as soon as `authStore.users` is
 * called and to cache indefinitely. In the future the `fetched` property can
 * be used to evaluate time-since-fetch for the purpose of re-fetching data.
 */

const useAuthStore = defineStore('auth', () => {
  const accessToken = useLocalStorage<string | null>('access_token', null),
    idToken = useLocalStorage<string | null>('id_token', null),
    refreshToken = useLocalStorage<string | null>('refresh_token', null),
    tokenExpiryDateString = useLocalStorage<string | null>(
      'token_expiry',
      null
    ),
    expiryDate = computed(() => {
      return tokenExpiryDateString.value
        ? new Date(tokenExpiryDateString.value)
        : undefined
    }),
    /**
     * To store any custom user fields
     */
    dateFormat: RemovableRef<string> = useLocalStorage('date_format', 'local'),
    timeZone: RemovableRef<string> = useLocalStorage('time_zone', LOCAL_TIME_ZONE),
    loading: Ref<boolean> = ref(false),
    fetched: Ref<Date | null> = ref(null)

  const getAccessToken = async (
    code: string | undefined,
    state: string | undefined
  ): Promise<void> => {
    if (!(code && state)) {
      throw new Error('Cannot fetch access token without code and state')
    }
    if (loading.value) {
      return Promise.resolve()
    }
    loading.value = true
    const code_verifier = CodeVerifier.get(state)
    CodeVerifier.set(state, null)
    if (code_verifier === null) {
      throw new Error('Unexpected verifier code')
    }
    return postTokenRequest(generateTokenEndpoint(), {
      grant_type: 'authorization_code',
      client_id: getEnv('VITE_OIDC_CLIENT_ID'),
      code: code,
      code_verifier,
      redirect_uri: `${window.location.origin}${window.location.pathname}`,
      scope: OIDC_SCOPES
    })
      .then((data) => {
        accessToken.value = data.access_token
        idToken.value = data.id_token
        refreshToken.value = data.refresh_token
        fetched.value = new Date()
        tokenExpiryDateString.value = dateInSecondsFromNow(
          data.expires_in
        ).toISOString()
        loading.value = false
      })
      .catch((err) => {
        Toasts.Error('Error fetching access token; See console for details')
        console.error(err)
      })
      .finally(() => (loading.value = false))
  }

  /**
   * `endSessionOnRefusal` says whether a refusal from the provider means the
   * session is over. Only the expiry watchdog passes it: that is the one caller
   * renewing a session we are relying on.
   */
  const exchangeRefreshToken = async function(
    { endSessionOnRefusal = false } = {}
  ): Promise<void> {
    if (loading.value || !isLoggedIn.value) {
      return Promise.resolve()
    }
    loading.value = true
    if (!refreshToken.value) {
      return Promise.reject(new Error('No refresh token'))
    }
    // Held so the refusal can be matched against what it was refusing.
    const attemptedRefreshToken = refreshToken.value
    return postTokenRequest(generateTokenEndpoint(), {
      client_id: getEnv('VITE_OIDC_CLIENT_ID'),
      grant_type: 'refresh_token',
      refresh_token: refreshToken.value
    })
      .then((data) => {
        accessToken.value = data.access_token
        if (data.id_token) {
          idToken.value = data.id_token
        }
        if (data.refresh_token) {
          refreshToken.value = data.refresh_token
        }
        tokenExpiryDateString.value = dateInSecondsFromNow(
          data.expires_in
        ).toISOString()
        loading.value = false
        fetched.value = new Date()
      })
      .catch((err) => {
        console.error(err)
        /*
          The provider refusing the grant means the session behind it is gone —
          it expired, or it was taken, which is what a disable does. Keeping the
          dead tokens would leave the tab apparently signed in until the access
          token expired. A network error carries no response, and must not sign
          anyone out.
        */
        const status = err instanceof TokenEndpointError ? err.status : undefined
        const refused = status === 400 || status === 401
        // The last clause keeps a refusal from taking a session it was not about:
        // a refresh rotates the token, so a call refused on a token that has since
        // been replaced was refused on a dead one, not on the live session.
        if (
          endSessionOnRefusal &&
          refused &&
          refreshToken.value === attemptedRefreshToken
        ) {
          clearSession()
          Toasts.Info('Session ended', 'Please sign in again')
          return
        }
        Toasts.Error('Error fetching refresh token; See console for details')
      })
      .finally(() => (loading.value = false))
  }

  const refreshIfExpiringInSeconds = 25

  const refreshIfExpiring = async (
    secondsInFuture: number = refreshIfExpiringInSeconds
  ) => {
    const now = dateInSecondsFromNow(secondsInFuture)
    if (!expiryDate.value || now >= expiryDate.value) {
      // The one caller renewing a session in use, so the only one for which a
      // refusal means that session is over.
      await exchangeRefreshToken({ endSessionOnRefusal: true })
    }
  }

  if (!initiated.expiryCheck) {
    setInterval(refreshIfExpiring, (refreshIfExpiringInSeconds - 1) * 1000)
    initiated.expiryCheck = true
  }

  /**
   * Drops every credential this tab holds. The id token goes with the rest:
   * anything that needs it as a logout hint must read it before calling this.
   */
  const clearSession = (): void => {
    Object.keys(localStorage)
      .filter((k: string) => k.startsWith('codeVerifier-'))
      .forEach((k) => localStorage.removeItem(k))
    accessToken.value = null
    // Cleared rather than set to now: a value still held here is written back to
    // local storage by its own watcher, after this has emptied it.
    tokenExpiryDateString.value = null
    refreshToken.value = null
    idToken.value = null
    ;['access_token', 'refresh_token', 'token_expiry', 'id_token'].forEach(
      (k) => localStorage.removeItem(k)
    )
    fetched.value = null
  }

  /**
   * Empties local and session storage rather than only the credentials, for an
   * account that has lost access: local storage also holds draft comments keyed
   * by finding, and session storage the path to return to after a sign-in. It
   * costs the user their preferences, which is the right trade there and the
   * wrong one for an ordinary sign-out — hence separate from `clearSession`.
   */
  const purgeLocalData = (): void => {
    clearSession()
    localStorage.clear()
    sessionStorage.clear()
  }

  const logout = async ({ purgeAll = false } = {}): Promise<void> => {
    loading.value = true
    const clear = () => (purgeAll ? purgeLocalData() : clearSession())
    try {
      const logoutUrl = await generateLogoutUrl(idToken.value)
      clear()
      window.location.href = logoutUrl
    } catch (err) {
      // The provider logout could not be reached; the local session still goes.
      clear()
      throw err
    }
  }

  function parseJwt(token: string | undefined): object {
    if (!token) {
      return {}
    }
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        })
        .join('')
    )

    return JSON.parse(jsonPayload)
  }

  const claims: ComputedRef = computed(() => {
    return accessToken.value ? parseJwt(accessToken.value) : null
  })

  // Roles the user holds, verbatim from the token
  const roles: ComputedRef<string[]> = computed(() => {
    const value = claims.value?.resource_access?.portal?.roles
    return Array.isArray(value) ? value : []
  })

  /**
   * The organization the user belongs to, title-cased for display, e.g.
   * "Galvanick". Empty when the token carries no organization.
   *
   * A portal user belongs to a single organization, so the first of
   * `organizations` is theirs. Taking it from the token means every signed-in
   * user has it, unlike the organization list behind `usersOrganizationsGet`,
   * which only admins may read.
   */
  const organizationName: ComputedRef<string> = computed(() => {
    const alias = organizations.value[0]
    if (!alias) return ''
    return alias
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((word) => word[0].toUpperCase() + word.slice(1))
      .join(' ')
  })

  /**
   * True when the current user may perform `target` — a named capability or a
   * single operation. Non-reactive, so it is safe anywhere `useAuthStore()` is
   * safe: router guards, watchers, event handlers. For reactive checks in
   * templates use `useCan` from `@/composables/useCan`.
   */
  const can = (target: Capability | Operation): boolean => {
    if (!useFeatureFlagsStore().rbacEnforced) return true
    return _can(target, roles.value)
  }

  /**
   * True when the current user may mutate data at all. Gates write affordances
   * against the read-only role, which qualifies for no guarded operation. Same
   * non-reactive contract as `can`; for templates use `useCanMutate` from
   * `@/composables/useCanMutate`.
   */
  const canMutate = (): boolean => {
    if (!useFeatureFlagsStore().rbacEnforced) return true
    return _canMutate(roles.value)
  }

  const currentUser: ComputedRef<User & { name: string } | undefined> = computed(() => {
    if (!isLoggedIn.value) {
      return undefined
    }
    const usersStore = useUsersStore()
    if (claims.value && typeof claims.value.username === 'string') {
      return usersStore.byId(claims.value.username)
    }
    return undefined
  })


  // Every id the access token gives for the caller.
  const callerIds: ComputedRef<Set<string>> = computed(() => {
    const ids = [claims.value?.sub, claims.value?.username]
    return new Set(
      ids.filter((id): id is string => typeof id === 'string' && id !== '')
    )
  })

  // Whether `id` is the signed-in user.
  const isCurrentUser = (id: string | undefined): boolean =>
    !!id && callerIds.value.has(id)

  const isLoggedIn: ComputedRef<boolean> = computed(
    () =>
      !!accessToken.value &&
      !!expiryDate.value &&
      expiryDate.value > new Date()
  )

  // Organizations from the access token's `organization` claim.
  const organizations: ComputedRef<string[]> = computed(() => {
    const value = claims.value?.organization
    if (!Array.isArray(value)) return []
    return value.filter(
      (entry: unknown): entry is string =>
        typeof entry === 'string' && entry !== ''
    )
  })

  const hasNoOrganization: ComputedRef<boolean> = computed(
    () => isLoggedIn.value && organizations.value.length === 0
  )

  const hasNoValidRole: ComputedRef<boolean> = computed(() => {
    if (!isLoggedIn.value) return false
    if (!useFeatureFlagsStore().rbacEnforced) return false
    return !roles.value.some((role) =>
      (ALL_ROLES as readonly string[]).includes(role)
    )
  })

  watch(currentUser, setUserContext)

  return {
    accessToken,
    can,
    canMutate,
    claims,
    clearSession,
    currentUser,
    dateFormat,
    exchangeRefreshToken,
    fetched,
    generateLoginUrl,
    generateLogoutUrl,
    getAccessToken,
    hasNoOrganization,
    hasNoValidRole,
    idToken,
    isCurrentUser,
    isLoggedIn,
    loading,
    logout,
    organizationName,
    organizations,
    purgeLocalData,
    refreshToken,
    roles,
    timeZone
  }
})

export default useAuthStore
